import { useState, useEffect, useRef } from "react";
import { Shield, MessageCircle } from "lucide-react";
import { EditBotSkeleton } from "../BotManagement/BotManagementSkeleton";
import { useParams } from "react-router-dom";
import { authService } from "../../../services/api";
import { useNotification } from "../../../components/Notifications/NotificationContext";
import { useTelegramValidation } from "../../../hooks/useTelegramValidation";
import { validateGroupIds, formatGroupId } from "../../../utils/groupIdValidator";
import { MediaManager } from '../../../components/MediaManager/MediaManager';
import { apiPlansToForm } from "../../../utils/apiPlansToForm";
import { useSyncBotSelection } from "../../../hooks/useSyncBotSelection";
import { useCopy } from '../../utils/text/copy';
import CfgModal from "../../../components/Modal/CfgModal";
import ToggleCard from "../../../components/UI/Toggle/ToggleCard";
import '../Welcome/index.css'
import './index.css'

function GroupCard({ icon, title, subtitle, value, onChange, groupChats, onRefresh, refreshing, className = "" }) {
    const configured = !!value;
    const selected = groupChats.find(g => String(g.chat_id) === String(value));

    return (
        <div className={`welcome-card ${className}`}>
            <div className="p-4">
                <div className="flex items-center gap-2 mb-1">
                    {icon}
                    <h3 className="font-semibold text-white text-sm">{title}</h3>
                </div>
                <p className="text-[11px] text-white/40 mb-3">{subtitle}</p>

                <div className="flex gap-2">
                    <select
                        className="form-select flex-1"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                    >
                        <option value="">Selecionar grupo...</option>
                        {groupChats.map(g => (
                            <option key={g.chat_id} value={g.chat_id}>
                                {g.title || g.chat_id} ({g.type})
                            </option>
                        ))}
                        {value && !selected && <option value={value}>{value}</option>}
                    </select>
                    <button
                        type="button"
                        title="Atualizar lista de grupos"
                        disabled={refreshing}
                        onClick={onRefresh}
                        className="shrink-0 w-9 h-9 flex items-center justify-center rounded-xl border border-white/[0.06] bg-white/[0.02] text-white/40 hover:text-white hover:border-white/10 transition-all disabled:opacity-50"
                    >
                        <i className={`fas fa-sync-alt text-xs ${refreshing ? 'fa-spin' : ''}`} />
                    </button>
                </div>

                <div className={`flex items-center gap-1.5 mt-2 text-[10px] ${configured ? 'text-[#22c55e]' : 'text-white/30'}`}>
                    <i className={`fas ${configured ? 'fa-check-circle' : 'fa-circle-notch'}`} />
                    <span>{configured ? 'Configurado' : 'Não configurado'}</span>
                </div>
            </div>
        </div>
    );
}

export default function EditBot() {
    const { botId } = useParams();
    const { showError, showSuccess } = useNotification();
    const { validate: validateTelegramBot, loading: validatingBot } = useTelegramValidation();


    // Estados do formulário
    const [token, setToken] = useState("");
    const [botUsername, setBotUsername] = useState("");
    const [isAntiCloneEnabled, setIsAntiCloneEnabled] = useState(false);
    const [startByAnyMessage, setStartByAnyMessage] = useState(false);
    const [vipGroupId, setVipGroupId] = useState("");
    const [logsGroupId, setLogsGroupId] = useState("");
    const [groupChats, setGroupChats] = useState([]);
    const [loadingGroupList, setLoadingGroupList] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingPage, setLoadingPage] = useState(true);
    const [botData, setBotData] = useState(null);
    const [showTokenModal, setShowTokenModal] = useState(false);


    useSyncBotSelection();
    // Carrega dados do bot ao inicializar
    useEffect(() => {
        if (!botId) return;
        loadBotData();
    }, [botId]);

    const loadBotData = async () => {
        try {
            setLoadingPage(true);
            const { data } = await authService.bots.edit_bot(botId);
            const bot = data.bot;
            setBotData(bot);

            setToken(bot.bot_token || "");
            setIsAntiCloneEnabled(bot.is_anti_clone_enabled || false);
            setStartByAnyMessage(bot.start_by_any_message || false);
            setBotUsername(bot.bot_username || "");
            setVipGroupId(bot.id_vip || "");
            setLogsGroupId(bot.id_logs || "");

            loadGroupChats();
        } catch (err) {
            console.error(err);
            showError("Erro ao carregar bot");
        } finally {
            setLoadingPage(false);
        }
    };

    const loadGroupChats = async () => {
        try {
            const { data } = await authService.listBotGroupChats(botId);
            setGroupChats(data.groups || []);
        } catch (err) {
            console.error(err);
        }
    };

    const handleRefreshGroupList = async () => {
        setLoadingGroupList(true);
        try {
            const { data } = await authService.refreshBotGroupChats(botId);
            setGroupChats(data.groups || []);
        } catch (err) {
            console.error(err);
            const msg = err?.response?.data?.error || "Erro ao atualizar lista de grupos";
            showError(msg);
        } finally {
            setLoadingGroupList(false);
        }
    };

    const maskToken = (token) => {
        if (!token || token.length <= 30) return token;
        return token.slice(0, 30) + "*****";
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Validar IDs de grupo
            const { isValid, errors } = validateGroupIds(vipGroupId, logsGroupId);
            if (!isValid) {
                errors.forEach(err => showError(err));
                setLoading(false);
                return;
            }

            // Validar bot nos grupos
            const result = await validateTelegramBot(botId, {
                vip_id: vipGroupId,
                logs_id: logsGroupId
            });
            if (!result.success) {
                setLoading(false);
                showError(result.error || "Validação do bot falhou. Verifique os IDs dos grupos e se o bot é administrador.");
                return;
            }
            showSuccess("Bot validado com sucesso nos grupos!");



            // Preparar dados do bot
            const botData = {
                token,
                vip_group_id: formatGroupId(vipGroupId),
                logs_group_id: formatGroupId(logsGroupId),
                is_anti_clone_enabled: isAntiCloneEnabled,
                start_by_any_message: startByAnyMessage,
            };

            // Atualizar dados do bot
            const { data: response } = await authService.bots.edit_bot_submit(botId, botData);

            if (!response.success) {
                throw new Error(response.error || "Erro ao atualizar bot");
            }


            showSuccess("Bot atualizado com sucesso!");
        } catch (err) {
            console.error(err);
            const errorMsg = err?.response?.data?.error || err?.message || "Erro ao editar bot";
            showError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>


            {loadingPage ? (
                <EditBotSkeleton />
            ) : (
                <>
                    <form className="spt-reveal" onSubmit={handleSubmit} style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}>

                        <div className="welcome-layout mt-2">
                            {/* ESQUERDA — form principal (token) */}
                            <div className="spt-form-bot welcome-main">
                                <div className="mt-4">
                                    <label className="spt-label">Token do Bot <span className="required">*</span></label>
                                    <div className="flex gap-2">
                                        <input
                                            value={token}
                                            onChange={(e) => setToken(e.target.value)}
                                            required
                                            className="hidden"
                                        />
                                        <input
                                            value={maskToken(token)}
                                            required
                                            readOnly
                                            className="input3 flex-1"
                                        />
                                        <button
                                            type="button"
                                            title="Editar Token"
                                            onClick={() => setShowTokenModal(true)}
                                            className="shrink-0 w-9 h-9 flex items-center my-auto justify-center rounded-xl border border-white/[0.06] bg-white/[0.02] text-white/40 hover:text-white hover:border-white/10 transition-all"
                                        >
                                            <i className="fas fa-edit text-xs" />
                                        </button>
                                    </div>
                                    <small className="form-hint">
                                        <span style={{ color: "#eab308" }}>
                                            <i className="fas fa-exclamation-triangle me-1"></i>Atenção:{" "}
                                        </span>
                                        Token obtido do @BotFather no Telegram (alterar reiniciará o bot)
                                    </small>
                                </div>

                                <div className="bot-toggles-group mt-8">
                                    <ToggleCard
                                        icon={<Shield size={16} />}
                                        title="Proteção Anti-Clonagem"
                                        checked={isAntiCloneEnabled}
                                        onChange={setIsAntiCloneEnabled}
                                    />
                                    <ToggleCard
                                        icon={<MessageCircle size={16} />}
                                        title="Iniciar por qualquer mensagem"
                                        checked={startByAnyMessage}
                                        onChange={setStartByAnyMessage}
                                    />
                                </div>
                            </div>

                            {/* DIREITA — cards de grupos */}
                            <aside className="welcome-side space-y-4 spt-reveal-stagger">
                                <GroupCard
                                    icon={
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-package text-blue-400" aria-hidden="true">
                                            <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z" />
                                            <path d="M12 22V12" />
                                            <polyline points="3.29 7 12 12 20.71 7" />
                                            <path d="m7.5 4.27 9 5.15" />
                                        </svg>
                                    }
                                    title="Grupo VIP"
                                    subtitle="Grupo/canal de acesso dos clientes"
                                    value={vipGroupId}
                                    onChange={setVipGroupId}
                                    groupChats={groupChats}
                                    onRefresh={handleRefreshGroupList}
                                    refreshing={loadingGroupList}
                                />
                                <GroupCard
                                    icon={
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-image text-[#f59e0b]" aria-hidden="true">
                                            <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                                            <circle cx="9" cy="9" r="2" />
                                            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                                        </svg>
                                    }
                                    title="Grupo de Notificações"
                                    subtitle="Recebe registros de vendas e mídias"
                                    value={logsGroupId}
                                    onChange={setLogsGroupId}
                                    groupChats={groupChats}
                                    onRefresh={handleRefreshGroupList}
                                    refreshing={loadingGroupList}
                                />
                            </aside>
                        </div>

                        <div className="flex justify-end mt-12">
                            <button disabled={loading || validatingBot} className="btn-primary">
                                {loading || validatingBot ? (
                                    <><i className="fas fa-spinner fa-spin me-2" /> Processando...</>
                                ) : (
                                    <><i className="fas fa-save me-2" /> Salvar Alterações</>
                                )}
                            </button>
                        </div>
                    </form>

                    {showTokenModal && (
                        <CfgModal
                            open={showTokenModal}
                            onClose={() => setShowTokenModal(false)}
                            bot={botData || { id: botId }}
                        />
                    )}

                </>)}
        </>
    )
};